import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, Building2, Calendar as CalendarIcon, Users, RotateCcw, Tag, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { DevelopmentService, Development } from "@/services/developmentService";
import { useToast } from "@/hooks/use-toast";
import { getErrorMessage, ErrorType } from "@/utils/errorMessages";
import { Checkbox } from "@/components/ui/checkbox";
/** Valor do select quando nenhum empreendimento está filtrado (página /venda). */
const EMPREENDIMENTO_TODOS = "__todos__";

// Função para parsear data sem conversão de timezone
// Recebe uma string no formato "yyyy-MM-dd" e retorna um Date no timezone local
const parseDateWithoutTimezone = (dateString: string): Date | null => {
  if (!dateString) return null;
  
  // Verificar se está no formato correto
  const dateRegex = /^(\d{4})-(\d{2})-(\d{2})$/;
  const match = dateString.match(dateRegex);
  
  if (!match) return null;
  
  // Extrair ano, mês e dia
  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10) - 1; // Mês é 0-indexed no JavaScript
  const day = parseInt(match[3], 10);
  
  // Criar Date usando componentes locais (não UTC)
  // Isso garante que a data seja exatamente a informada, sem conversão de timezone
  const date = new Date(year, month, day);
  
  // Validar se a data é válida
  if (isNaN(date.getTime())) return null;
  
  return date;
};

interface SearchBarProps {
  compactHeight?: boolean;
  /** Página /venda — busca só cotas à venda */
  saleSearch?: boolean;
}

const SearchBar = ({ compactHeight = false, saleSearch = false }: SearchBarProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [developments, setDevelopments] = useState<Development[]>([]);
  const [loadingDevelopments, setLoadingDevelopments] = useState(true);
  const [empreendimento, setEmpreendimento] = useState<string>(
    saleSearch ? "" : "Porto 2 Life",
  );
  const [dataInicio, setDataInicio] = useState<Date | undefined>();
  const [dataFim, setDataFim] = useState<Date | undefined>();
  const [hospedes, setHospedes] = useState("1");
  const [tipoServico, setTipoServico] = useState<string>("");
  const [buscarDatasExatas, setBuscarDatasExatas] = useState(false);
  const [initialized, setInitialized] = useState(false);

  const developmentsSorted = useMemo(
    () =>
      [...developments].sort((a, b) =>
        a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" }),
      ),
    [developments],
  );

  const empreendimentoSelectValue =
    saleSearch && !empreendimento ? EMPREENDIMENTO_TODOS : empreendimento;

  // Carregar empreendimentos ativos
  useEffect(() => {
    const loadDevelopments = async () => {
      try {
        setLoadingDevelopments(true);
        const allDevelopments = await DevelopmentService.getAllDevelopments();
        setDevelopments(allDevelopments);
        
        // Encontrar "Porto 2 Life" e definir como padrão
        const porto2Life = allDevelopments.find(d => 
          d.name === 'Porto 2 Life' ||
          d.name.toLowerCase() === 'porto 2 life' ||
          d.name.toLowerCase().includes('porto 2 life') || 
          d.name.toLowerCase().includes('porto2life')
        );
        
        if (saleSearch) {
          const empreendimentoParam = new URLSearchParams(
            window.location.search,
          ).get("empreendimento");
          if (!empreendimentoParam) {
            setEmpreendimento("");
          }
        } else if (porto2Life) {
          setEmpreendimento(porto2Life.name);
        } else if (allDevelopments.length > 0) {
          setEmpreendimento(allDevelopments[0].name);
        } else {
          setEmpreendimento("Porto 2 Life");
        }
      } catch (error) {
        console.error('Erro ao carregar empreendimentos:', error);
      } finally {
        setLoadingDevelopments(false);
      }
    };

    loadDevelopments();
  }, []);

  // Preencher campos com parâmetros da URL APENAS na primeira renderização
  // Não atualizar quando os parâmetros mudarem após a busca (para não sobrescrever as datas do usuário)
  useEffect(() => {
    if (!initialized && !loadingDevelopments) {
      const inicioParam = searchParams.get("inicio");
      const fimParam = searchParams.get("fim");
      const hospedesParam = searchParams.get("hospedes");
      const empreendimentoParam = searchParams.get("empreendimento");
      const tipoServicoParam = searchParams.get("tipoServico");
      const buscarDatasExatasParam = searchParams.get("buscarDatasExatas");

      if (empreendimentoParam) {
        const trimmed = empreendimentoParam.trim();
        const found = developments.find((d) => d.name === trimmed);
        setEmpreendimento(found?.name ?? trimmed);
      } else if (saleSearch) {
        setEmpreendimento("");
      }

      if (inicioParam) {
        // Usar função que parseia sem conversão de timezone
        const data = parseDateWithoutTimezone(inicioParam);
        if (data) {
          setDataInicio(data);
        }
      }
      if (fimParam) {
        // Usar função que parseia sem conversão de timezone
        const data = parseDateWithoutTimezone(fimParam);
        if (data) {
          setDataFim(data);
        }
      }
      if (hospedesParam) {
        setHospedes(hospedesParam);
      }
      if (saleSearch) {
        setTipoServico("venda");
      } else if (tipoServicoParam) {
        setTipoServico(tipoServicoParam);
      }
      if (buscarDatasExatasParam === "true") {
        setBuscarDatasExatas(true);
      }
      setInitialized(true);
    }
  }, [searchParams, initialized, loadingDevelopments, developments, saleSearch]);

  const isVendaMode = saleSearch || tipoServico === "venda";

  const handleTipoServicoChange = (value: string) => {
    setTipoServico(value);
    if (value === "venda") {
      setDataInicio(undefined);
      setDataFim(undefined);
      setBuscarDatasExatas(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();

    const empreendimentoName = empreendimento.trim();

    if (isVendaMode) {
      const params = new URLSearchParams();
      if (empreendimentoName) {
        params.set("empreendimento", empreendimentoName);
      }
      if (hospedes && hospedes !== "1" && hospedes !== "") {
        params.set("hospedes", hospedes);
      }
      navigate(`/venda?${params.toString()}`);
      return;
    }
    
    // Validação: check-out deve ser posterior a check-in
    if (dataInicio && dataFim) {
      if (dataFim <= dataInicio) {
        const errorMessage = getErrorMessage(ErrorType.VALIDATION_DATE_RANGE);
        toast({
          title: errorMessage.title,
          description: errorMessage.description,
          variant: errorMessage.variant || "destructive",
        });
        return;
      }
    }
    
    const params = new URLSearchParams();
    
    if (empreendimentoName) {
      params.set("empreendimento", empreendimentoName);
    }
    
    // Incluir apenas filtros que foram REALMENTE preenchidos
    if (dataInicio) {
      params.set("inicio", format(dataInicio, "yyyy-MM-dd"));
    }
    if (dataFim) {
      params.set("fim", format(dataFim, "yyyy-MM-dd"));
    }
    if (hospedes && hospedes !== "1" && hospedes !== "") {
      params.set("hospedes", hospedes);
    }
    if (tipoServico && tipoServico !== "") {
      params.set("tipoServico", tipoServico);
    }
    if (buscarDatasExatas) {
      params.set("buscarDatasExatas", "true");
    }
    
    console.log('🔍 Parâmetros de busca enviados:', {
      empreendimento,
      dataInicio: dataInicio ? format(dataInicio, "yyyy-MM-dd") : null,
      dataFim: dataFim ? format(dataFim, "yyyy-MM-dd") : null,
      hospedes: hospedes !== "1" && hospedes !== "" ? hospedes : null,
      tipoServico: tipoServico || null,
      buscarDatasExatas
    });
    
    navigate(`/busca?${params.toString()}`);
  };

  const handleClearFilters = () => {
    if (saleSearch) {
      setEmpreendimento("");
    } else {
      const porto2Life = developments.find(
        (d) =>
          d.name.toLowerCase().includes("porto 2 life") ||
          d.name.toLowerCase().includes("porto2life"),
      );
      if (porto2Life) {
        setEmpreendimento(porto2Life.name);
      } else if (developments.length > 0) {
        setEmpreendimento(developments[0].name);
      }
    }
    setDataInicio(undefined);
    setDataFim(undefined);
    setHospedes("1");
    setTipoServico(saleSearch ? "venda" : "");
    setBuscarDatasExatas(false);
    console.log('🧹 Filtros limpos');
  };

  const controlHeightClass = compactHeight ? "h-9 sm:h-10" : "h-11 sm:h-12";
  const datesDisabledClass = isVendaMode
    ? "opacity-50 pointer-events-none select-none"
    : "";
  const labelSpacingClass = compactHeight
    ? "mb-1 sm:mb-1.5"
    : "mb-1.5 sm:mb-2";
  const footerSpacingClass = compactHeight ? "mt-2 sm:mt-3" : "mt-3 sm:mt-4";

  return (
    <div className="flex justify-center w-full px-0">
      <form
        onSubmit={handleSearch}
        className="w-full bg-card/40 backdrop-blur-sm rounded-2xl shadow-large px-3 py-2 sm:px-4 sm:py-3 md:px-6 md:py-4 lg:px-8 lg:py-5 mx-auto"
      >
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 sm:gap-4 items-end">
        <div className="md:col-span-2">
          <label className={`block text-xs sm:text-sm font-medium ${labelSpacingClass} text-foreground`}>
            <Tag className="inline h-3 w-3 sm:h-4 sm:w-4 mr-1" />
            Tipo de Serviço
          </label>
          <Select
            value={isVendaMode ? "venda" : tipoServico || undefined}
            onValueChange={handleTipoServicoChange}
            disabled={saleSearch}
          >
            <SelectTrigger className={`${controlHeightClass} text-sm`}>
              <SelectValue placeholder="Todos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="aluguel">Aluguel</SelectItem>
              <SelectItem value="troca">Troca</SelectItem>
              <SelectItem value="troca_aluguel">Troca/Aluguel</SelectItem>
              <SelectItem value="venda">Venda</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="md:col-span-2">
          <label className={`block text-xs sm:text-sm font-medium ${labelSpacingClass} text-foreground`}>
            <Building2 className="inline h-3 w-3 sm:h-4 sm:w-4 mr-1" />
            Empreendimento
          </label>
          <Select
            value={empreendimentoSelectValue || undefined}
            onValueChange={(v) =>
              setEmpreendimento(v === EMPREENDIMENTO_TODOS ? "" : v)
            }
            disabled={loadingDevelopments}
          >
            <SelectTrigger className={`${controlHeightClass} text-sm`}>
              <SelectValue
                placeholder={
                  loadingDevelopments ? "Carregando..." : "Selecione o empreendimento"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {saleSearch && (
                <SelectItem value={EMPREENDIMENTO_TODOS}>
                  Todos os empreendimentos
                </SelectItem>
              )}
              {developmentsSorted.map((development) => (
                <SelectItem key={development.id} value={development.name}>
                  {development.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className={cn("grid grid-cols-2 gap-2 sm:gap-3 md:col-span-4 md:grid-cols-2", datesDisabledClass)}>
          <div>
            <label className={`block text-xs sm:text-sm font-medium ${labelSpacingClass} text-foreground`}>
              <CalendarIcon className="inline h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              Entrada
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    `${controlHeightClass} w-full rounded-md border border-input bg-background px-3 py-2 inline-flex items-center justify-start text-left font-normal overflow-hidden text-sm transition-none hover:bg-background hover:text-foreground active:bg-background focus:outline-none`,
                    !dataInicio && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="truncate">
                    {dataInicio ? format(dataInicio, "dd/MM/yyyy", { locale: ptBR }) : "Selecione a data"}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto rounded-xl border bg-white p-0 shadow-xl" align="start">
                <div className="flex items-center gap-2 border-b px-3 py-2 text-sm font-medium text-foreground">
                  <Sparkles className="h-4 w-4 text-primary" />
                  Escolha a data de entrada
                </div>
                <Calendar
                  mode="single"
                  selected={dataInicio}
                  onSelect={setDataInicio}
                  initialFocus
                  disabled={(date) => date < new Date()}
                  locale={ptBR}
                  showOutsideDays
                  className="pointer-events-auto p-2"
                />
              </PopoverContent>
            </Popover>
          </div>

          <div>
            <label className={`block text-xs sm:text-sm font-medium ${labelSpacingClass} text-foreground`}>
              <CalendarIcon className="inline h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              Saída
            </label>
            <Popover>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  className={cn(
                    `${controlHeightClass} w-full rounded-md border border-input bg-background px-3 py-2 inline-flex items-center justify-start text-left font-normal overflow-hidden text-sm transition-none hover:bg-background hover:text-foreground active:bg-background focus:outline-none`,
                    !dataFim && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
                  <span className="truncate">
                    {dataFim ? format(dataFim, "dd/MM/yyyy", { locale: ptBR }) : "Selecione a data"}
                  </span>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto rounded-xl border bg-white p-0 shadow-xl" align="start">
                <div className="flex items-center gap-2 border-b px-3 py-2 text-sm font-medium text-foreground">
                  <Sparkles className="h-4 w-4 text-primary" />
                  Escolha a data de saída
                </div>
                <Calendar
                  mode="single"
                  selected={dataFim}
                  onSelect={(date) => {
                    setDataFim(date);
                    // Validação em tempo real: se check-out for anterior ou igual a check-in, mostrar erro
                    if (date && dataInicio && date <= dataInicio) {
                      const errorMessage = getErrorMessage(ErrorType.VALIDATION_DATE_RANGE);
                      toast({
                        title: errorMessage.title,
                        description: errorMessage.description,
                        variant: errorMessage.variant || "destructive",
                      });
                    }
                  }}
                  initialFocus
                  disabled={(date) => date < (dataInicio || new Date())}
                  locale={ptBR}
                  showOutsideDays
                  className="pointer-events-auto p-2"
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div className="md:col-span-2">
          <label className={`block text-xs sm:text-sm font-medium ${labelSpacingClass} text-foreground whitespace-nowrap`}>
            <Users className="inline h-3 w-3 sm:h-4 sm:w-4 mr-1" />
            Hóspedes
          </label>
          <Input
            type="number"
            min="1"
            max="20"
            value={hospedes}
            onChange={(e) => setHospedes(e.target.value)}
            className={`${controlHeightClass} text-sm`}
            placeholder="Quantos?"
          />
        </div>

        <div className="md:col-span-2 flex gap-2">
          <Button type="submit" className={`flex-1 ${controlHeightClass} gap-2 text-sm sm:text-base font-semibold`}>
            <Search className="h-4 w-4 sm:h-5 sm:w-5" />
            Buscar
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={handleClearFilters}
            className={`${controlHeightClass} px-2 sm:px-3 gap-2 text-xs sm:text-sm`}
            title="Limpar filtros"
          >
            <RotateCcw className="h-3 w-3 sm:h-4 sm:w-4" />
            <span className="hidden sm:inline">Limpar</span>
          </Button>
        </div>
      </div>

      {!isVendaMode && (
        <div className={`${footerSpacingClass} flex items-center space-x-2`}>
          <Checkbox
            id="buscarDatasExatas"
            checked={buscarDatasExatas}
            onCheckedChange={(checked) => setBuscarDatasExatas(checked === true)}
          />
          <label
            htmlFor="buscarDatasExatas"
            className="text-xs sm:text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
          >
            Buscar Datas Exatas
          </label>
        </div>
      )}
    </form>
    </div>
  );
};

export default SearchBar;
